import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { 
  Calendar, Users, FolderKanban, LogOut, Plus, Edit, Trash2, 
  RefreshCw, Home, Mic, Building2, Mail, Phone, UserPlus
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [csrInquiries, setCsrInquiries] = useState([]);
  const [sessionRegistrations, setSessionRegistrations] = useState([]);
  
  const token = localStorage.getItem("adminToken");

  const axiosAuth = axios.create({
    headers: { Authorization: `Bearer ${token}` }
  });

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    fetchAllData();
  }, [token, navigate]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [eventsRes, speakersRes, projectsRes, csrRes, sessionRes] = await Promise.all([
        axiosAuth.get(`${API}/admin/events`),
        axiosAuth.get(`${API}/admin/speakers`),
        axiosAuth.get(`${API}/admin/projects`),
        axiosAuth.get(`${API}/admin/csr-inquiries`),
        axiosAuth.get(`${API}/admin/session-registrations`)
      ]);
      setEvents(eventsRes.data);
      setSpeakers(speakersRes.data);
      setProjects(projectsRes.data);
      setCsrInquiries(csrRes.data);
      setSessionRegistrations(sessionRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        localStorage.removeItem("adminToken");
        navigate("/admin/login");
      } else {
        toast.error("Failed to fetch data");
      }
    } finally {
      setLoading(false);
    }
  };

  const seedData = async () => {
    try {
      await axiosAuth.post(`${API}/admin/seed`);
      toast.success("Seed data created successfully!");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to seed data");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/admin/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_48bcd539-ef2e-44cd-bd5d-721585fa8242/artifacts/5hg2aby0_image.png" 
              alt="CLIC Logo" 
              className="h-8 w-auto"
            />
            <span className="font-semibold text-[#1E293B]">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => navigate("/")} data-testid="view-site-btn">
              <Home className="w-4 h-4 mr-2" />
              View Site
            </Button>
            <Button variant="outline" size="sm" onClick={fetchAllData} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <StatCard icon={Building2} label="CSR Inquiries" value={csrInquiries.length} color="teal" />
          <StatCard icon={UserPlus} label="Session Signups" value={sessionRegistrations.length} color="orange" />
          <StatCard icon={Calendar} label="Events" value={events.length} color="blue" />
          <StatCard icon={Mic} label="Speakers" value={speakers.length} color="purple" />
          <StatCard icon={FolderKanban} label="Projects" value={projects.length} color="green" />
        </div>

        {/* Tabs */}
        <Tabs defaultValue="csr-inquiries" className="space-y-6">
          <TabsList className="bg-white border">
            <TabsTrigger value="csr-inquiries" data-testid="tab-csr">
              <Building2 className="w-4 h-4 mr-2" />
              CSR Inquiries
            </TabsTrigger>
            <TabsTrigger value="session-registrations" data-testid="tab-sessions">
              <UserPlus className="w-4 h-4 mr-2" />
              Session Signups
            </TabsTrigger>
            <TabsTrigger value="events" data-testid="tab-events">
              <Calendar className="w-4 h-4 mr-2" />
              Events
            </TabsTrigger>
            <TabsTrigger value="speakers" data-testid="tab-speakers">
              <Mic className="w-4 h-4 mr-2" />
              Speakers
            </TabsTrigger>
            <TabsTrigger value="projects" data-testid="tab-projects">
              <FolderKanban className="w-4 h-4 mr-2" />
              Projects
            </TabsTrigger>
          </TabsList>

          {/* CSR Inquiries Tab */}
          <TabsContent value="csr-inquiries">
            <CSRInquiriesView inquiries={csrInquiries} axiosAuth={axiosAuth} onRefresh={fetchAllData} />
          </TabsContent>

          {/* Session Registrations Tab */}
          <TabsContent value="session-registrations">
            <SessionRegistrationsView registrations={sessionRegistrations} />
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events">
            <EventsManager events={events} axiosAuth={axiosAuth} onRefresh={fetchAllData} />
          </TabsContent>

          {/* Speakers Tab */}
          <TabsContent value="speakers">
            <SpeakersManager speakers={speakers} axiosAuth={axiosAuth} onRefresh={fetchAllData} />
          </TabsContent>

          {/* Projects Tab */}
          <TabsContent value="projects">
            <ProjectsManager projects={projects} axiosAuth={axiosAuth} onRefresh={fetchAllData} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, color }) => {
  const colorClasses = {
    teal: "bg-[#1F7A5A]/10 text-[#1F7A5A]",
    blue: "bg-blue-100 text-blue-600",
    purple: "bg-purple-100 text-purple-600",
    green: "bg-[#1F7A5A]/10 text-[#1F7A5A]",
    orange: "bg-[#F4C430]/20 text-[#92400E]"
  };

  return (
    <Card className="p-6 border-0 shadow-sm">
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <p className="text-2xl font-bold text-[#1E293B]">{value}</p>
          <p className="text-sm text-slate-500">{label}</p>
        </div>
      </div>
    </Card>
  );
};

// CSR Inquiries View Component
const CSRInquiriesView = ({ inquiries, axiosAuth, onRefresh }) => {
  const updateStatus = async (id, status) => {
    try {
      await axiosAuth.put(`${API}/admin/csr-inquiries/${id}/status?status=${status}`);
      toast.success("Status updated!");
      onRefresh();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: "bg-blue-100 text-blue-700",
      contacted: "bg-yellow-100 text-yellow-700",
      in_discussion: "bg-purple-100 text-purple-700",
      converted: "bg-green-100 text-green-700",
      closed: "bg-slate-100 text-slate-700"
    };
    return colors[status] || "bg-slate-100 text-slate-700";
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <Card className="p-6 border-0 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-semibold text-lg text-[#1E293B]">CSR Partnership Inquiries</h3>
        <Badge variant="outline" className="text-[#1F7A5A] border-[#1F7A5A]">
          {inquiries.filter(i => i.status === "new").length} new
        </Badge>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Contact</TableHead>
            <TableHead>Organization</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {inquiries.map((inquiry) => (
            <TableRow key={inquiry.id}>
              <TableCell>
                <div>
                  <p className="font-medium text-[#1E293B]">{inquiry.name}</p>
                  <div className="flex items-center gap-3 text-sm text-slate-500">
                    <a href={`mailto:${inquiry.email}`} className="flex items-center gap-1 hover:text-[#0D9488]">
                      <Mail className="w-3 h-3" />
                      {inquiry.email}
                    </a>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-slate-500">
                    <Phone className="w-3 h-3" />
                    {inquiry.phone}
                  </div>
                </div>
              </TableCell>
              <TableCell className="font-medium">{inquiry.organization}</TableCell>
              <TableCell>{inquiry.role}</TableCell>
              <TableCell className="text-slate-500 text-sm">{formatDate(inquiry.created_at)}</TableCell>
              <TableCell>
                <Badge className={getStatusColor(inquiry.status)}>
                  {inquiry.status.replace("_", " ")}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <Select value={inquiry.status} onValueChange={(value) => updateStatus(inquiry.id, value)}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="in_discussion">In Discussion</SelectItem>
                    <SelectItem value="converted">Converted</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </TableCell>
            </TableRow>
          ))}
          {inquiries.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                No CSR inquiries yet. Share your partnership page to get leads!
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Card>
  );
};

// Session Registrations View Component
const SessionRegistrationsView = ({ registrations }) => {
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <Card className="p-6 border-0 shadow-sm" data-testid="session-registrations-table">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-semibold text-lg text-[#1E293B]">Live Session Registrations</h3>
        <Badge variant="outline" className="text-[#F4C430] border-[#F4C430]">
          {registrations.length} total
        </Badge>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Organisation</TableHead>
            <TableHead>Designation</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Registered On</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {registrations.map((reg) => (
            <TableRow key={reg.id}>
              <TableCell className="font-medium text-[#1E293B]">{reg.name}</TableCell>
              <TableCell>{reg.organization}</TableCell>
              <TableCell>{reg.designation}</TableCell>
              <TableCell>
                <a href={`mailto:${reg.email}`} className="flex items-center gap-1 text-sm text-slate-600 hover:text-[#1F7A5A]">
                  <Mail className="w-3 h-3" />
                  {reg.email}
                </a>
              </TableCell>
              <TableCell className="text-slate-500 text-sm">{formatDate(reg.registered_at)}</TableCell>
            </TableRow>
          ))}
          {registrations.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                No session registrations yet. Promote your live sessions to get signups!
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Card>
  );
};

// Events Manager Component
const EventsManager = ({ events, axiosAuth, onRefresh }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    subtitle: "",
    description: "",
    event_date: "",
    event_time: "",
    duration: "60 mins",
    platform: "Zoom",
    meeting_link: "",
    is_active: true
  });

  const resetForm = () => {
    setFormData({
      title: "",
      subtitle: "",
      description: "",
      event_date: "",
      event_time: "",
      duration: "60 mins",
      platform: "Zoom",
      meeting_link: "",
      is_active: true
    });
    setEditingEvent(null);
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData(event);
    setIsOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEvent) {
        await axiosAuth.put(`${API}/admin/events/${editingEvent.id}`, formData);
        toast.success("Event updated!");
      } else {
        await axiosAuth.post(`${API}/admin/events`, formData);
        toast.success("Event created!");
      }
      setIsOpen(false);
      resetForm();
      onRefresh();
    } catch (error) {
      toast.error("Failed to save event");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Are you sure you want to delete this event?")) return;
    try {
      await axiosAuth.delete(`${API}/admin/events/${id}`);
      toast.success("Event deleted!");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete event");
    }
  };

  return (
    <Card className="p-6 border-0 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-semibold text-lg text-[#1E293B]">Manage Events</h3>
        <Dialog open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#1F7A5A] hover:bg-[#186347] text-white" data-testid="add-event-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Event
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingEvent ? "Edit Event" : "Add New Event"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Title</Label>
                  <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required />
                </div>
                <div className="col-span-2">
                  <Label>Subtitle</Label>
                  <Input value={formData.subtitle} onChange={(e) => setFormData({...formData, subtitle: e.target.value})} required />
                </div>
                <div className="col-span-2">
                  <Label>Description</Label>
                  <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} required />
                </div>
                <div>
                  <Label>Event Date</Label>
                  <Input type="date" value={formData.event_date} onChange={(e) => setFormData({...formData, event_date: e.target.value})} required />
                </div>
                <div>
                  <Label>Event Time</Label>
                  <Input placeholder="e.g., 11:00 AM IST" value={formData.event_time} onChange={(e) => setFormData({...formData, event_time: e.target.value})} required />
                </div>
                <div>
                  <Label>Duration</Label>
                  <Input placeholder="e.g., 60 mins" value={formData.duration} onChange={(e) => setFormData({...formData, duration: e.target.value})} required />
                </div>
                <div>
                  <Label>Platform</Label>
                  <Input placeholder="e.g., Zoom" value={formData.platform} onChange={(e) => setFormData({...formData, platform: e.target.value})} required />
                </div>
                <div className="col-span-2">
                  <Label>Meeting Link</Label>
                  <Input placeholder="https://zoom.us/j/..." value={formData.meeting_link} onChange={(e) => setFormData({...formData, meeting_link: e.target.value})} required />
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={formData.is_active} onCheckedChange={(checked) => setFormData({...formData, is_active: checked})} />
                  <Label>Active Event</Label>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => { setIsOpen(false); resetForm(); }}>Cancel</Button>
                <Button type="submit" className="bg-[#1F7A5A] hover:bg-[#186347] text-white">Save Event</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Title</TableHead>
            <TableHead>Date & Time</TableHead>
            <TableHead>Platform</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {events.map((event) => (
            <TableRow key={event.id}>
              <TableCell className="font-medium">{event.title}</TableCell>
              <TableCell>{event.event_date} · {event.event_time}</TableCell>
              <TableCell>{event.platform}</TableCell>
              <TableCell>
                <Badge variant={event.is_active ? "default" : "secondary"}>
                  {event.is_active ? "Active" : "Inactive"}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <Button variant="ghost" size="sm" onClick={() => handleEdit(event)}>
                  <Edit className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(event.id)}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
          {events.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                No events found. Add your first event!
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Card>
  );
};

// Speakers Manager Component
const SpeakersManager = ({ speakers, axiosAuth, onRefresh }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [editingSpeaker, setEditingSpeaker] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    role: "",
    bio: "",
    image_url: "",
    achievements: [],
    order: 0
  });
  const [achievementInput, setAchievementInput] = useState("");

  const resetForm = () => {
    setFormData({ name: "", role: "", bio: "", image_url: "", achievements: [], order: 0 });
    setEditingSpeaker(null);
    setAchievementInput("");
  };

  const handleEdit = (speaker) => {
    setEditingSpeaker(speaker);
    setFormData(speaker);
    setIsOpen(true);
  };

  const addAchievement = () => {
    if (achievementInput.trim()) {
      setFormData({...formData, achievements: [...formData.achievements, achievementInput.trim()]});
      setAchievementInput("");
    }
  };

  const removeAchievement = (index) => {
    setFormData({...formData, achievements: formData.achievements.filter((_, i) => i !== index)});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSpeaker) {
        await axiosAuth.put(`${API}/admin/speakers/${editingSpeaker.id}`, formData);
        toast.success("Speaker updated!");
      } else {
        await axiosAuth.post(`${API}/admin/speakers`, formData);
        toast.success("Speaker created!");
      }
      setIsOpen(false);
      resetForm();
      onRefresh();
    } catch (error) {
      toast.error("Failed to save speaker");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Are you sure you want to delete this speaker?")) return;
    try {
      await axiosAuth.delete(`${API}/admin/speakers/${id}`);
      toast.success("Speaker deleted!");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete speaker");
    }
  };

  return (
    <Card className="p-6 border-0 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-semibold text-lg text-[#1E293B]">Manage Speakers</h3>
        <Dialog open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#1F7A5A] hover:bg-[#186347] text-white" data-testid="add-speaker-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Speaker
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingSpeaker ? "Edit Speaker" : "Add New Speaker"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
                </div>
                <div>
                  <Label>Role</Label>
                  <Input value={formData.role} onChange={(e) => setFormData({...formData, role: e.target.value})} required />
                </div>
                <div className="col-span-2">
                  <Label>Bio</Label>
                  <Textarea value={formData.bio} onChange={(e) => setFormData({...formData, bio: e.target.value})} required />
                </div>
                <div className="col-span-2">
                  <Label>Image URL</Label>
                  <Input value={formData.image_url} onChange={(e) => setFormData({...formData, image_url: e.target.value})} required />
                </div>
                <div>
                  <Label>Display Order</Label>
                  <Input type="number" value={formData.order} onChange={(e) => setFormData({...formData, order: parseInt(e.target.value)})} />
                </div>
                <div className="col-span-2">
                  <Label>Achievements</Label>
                  <div className="flex gap-2 mb-2">
                    <Input value={achievementInput} onChange={(e) => setAchievementInput(e.target.value)} placeholder="Add achievement" />
                    <Button type="button" variant="outline" onClick={addAchievement}>Add</Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.achievements.map((ach, i) => (
                      <Badge key={i} variant="secondary" className="cursor-pointer" onClick={() => removeAchievement(i)}>
                        {ach} ×
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => { setIsOpen(false); resetForm(); }}>Cancel</Button>
                <Button type="submit" className="bg-[#1F7A5A] hover:bg-[#186347] text-white">Save Speaker</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {speakers.map((speaker) => (
          <Card key={speaker.id} className="p-4 border">
            <div className="flex items-start gap-4">
              <img src={speaker.image_url} alt={speaker.name} className="w-16 h-16 rounded-full object-cover" 
                onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${speaker.name}&background=0D9488&color=fff`; }} />
              <div className="flex-1">
                <h4 className="font-semibold text-[#1E293B]">{speaker.name}</h4>
                <p className="text-sm text-slate-500">{speaker.role}</p>
                <p className="text-xs text-slate-400 mt-1">Order: {speaker.order}</p>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => handleEdit(speaker)}>
                  <Edit className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(speaker.id)}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
        {speakers.length === 0 && (
          <div className="col-span-full text-center text-slate-500 py-8">
            No speakers found. Add your first speaker!
          </div>
        )}
      </div>
    </Card>
  );
};

// Projects Manager Component
const ProjectsManager = ({ projects, axiosAuth, onRefresh }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "theme",
    goal_amount: 0,
    raised_amount: 0,
    is_active: true,
    order: 0
  });

  const resetForm = () => {
    setFormData({ title: "", description: "", category: "theme", goal_amount: 0, raised_amount: 0, is_active: true, order: 0 });
    setEditingProject(null);
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData(project);
    setIsOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProject) {
        await axiosAuth.put(`${API}/admin/projects/${editingProject.id}`, formData);
        toast.success("Project updated!");
      } else {
        await axiosAuth.post(`${API}/admin/projects`, formData);
        toast.success("Project created!");
      }
      setIsOpen(false);
      resetForm();
      onRefresh();
    } catch (error) {
      toast.error("Failed to save project");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Are you sure you want to delete this project?")) return;
    try {
      await axiosAuth.delete(`${API}/admin/projects/${id}`);
      toast.success("Project deleted!");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete project");
    }
  };

  const formatAmount = (amount) => {
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(0)}K`;
    return `₹${amount}`;
  };

  return (
    <Card className="p-6 border-0 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-semibold text-lg text-[#1E293B]">Manage Projects</h3>
        <Dialog open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#1F7A5A] hover:bg-[#186347] text-white" data-testid="add-project-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Project
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingProject ? "Edit Project" : "Add New Project"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Title</Label>
                  <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required />
                </div>
                <div className="col-span-2">
                  <Label>Description</Label>
                  <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} required />
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="state">State Program</SelectItem>
                      <SelectItem value="theme">Theme Program</SelectItem>
                      <SelectItem value="policy">Policy Initiative</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Display Order</Label>
                  <Input type="number" value={formData.order} onChange={(e) => setFormData({...formData, order: parseInt(e.target.value)})} />
                </div>
                <div>
                  <Label>Goal Amount (₹)</Label>
                  <Input type="number" value={formData.goal_amount} onChange={(e) => setFormData({...formData, goal_amount: parseFloat(e.target.value)})} required />
                </div>
                <div>
                  <Label>Raised Amount (₹)</Label>
                  <Input type="number" value={formData.raised_amount} onChange={(e) => setFormData({...formData, raised_amount: parseFloat(e.target.value)})} />
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={formData.is_active} onCheckedChange={(checked) => setFormData({...formData, is_active: checked})} />
                  <Label>Active</Label>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => { setIsOpen(false); resetForm(); }}>Cancel</Button>
                <Button type="submit" className="bg-[#1F7A5A] hover:bg-[#186347] text-white">Save Project</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Project</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Progress</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {projects.map((project) => (
            <TableRow key={project.id}>
              <TableCell className="font-medium">{project.title}</TableCell>
              <TableCell>
                <Badge variant="outline">{project.category}</Badge>
              </TableCell>
              <TableCell>
                {formatAmount(project.raised_amount)} / {formatAmount(project.goal_amount)}
                <span className="text-slate-400 ml-1">({Math.round((project.raised_amount / project.goal_amount) * 100)}%)</span>
              </TableCell>
              <TableCell>
                <Badge variant={project.is_active ? "default" : "secondary"}>
                  {project.is_active ? "Active" : "Inactive"}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <Button variant="ghost" size="sm" onClick={() => handleEdit(project)}>
                  <Edit className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(project.id)}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
          {projects.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                No projects found. Add your first project!
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Card>
  );
};

export default AdminDashboard;
